import React, { useState, useEffect } from 'react';

import { DialogTitle, TextField, Button, Typography, OTCInput, Alert } from '@bessemercws/components/bui';
import { ERRORSTATE } from '@bessemercws/graphql';
import { useSnackbarContext } from '@bessemercws/utils';
import { Controller, useForm } from 'react-hook-form';

import { useUserVerificationContext } from '#/components/UserVerificationContext/UserVerificationContext';
import {
  dialogSubTitle,
  dialogContentTwo,
  dialogContent,
  dialogContentThree,
  dialogTitle,
  dialogTitleTwo,
  cancelButton,
  saveButton,
  dialogTitleThree,
  otcDialogContenet,
  newCodeButton,
  validateButton,
  confirmButton,
} from '#/constants/userVerificationConstant';
import { WizardStep } from '#/models/WizardStep.enum';
import { StyledDialogActions, StyledDialogContent } from './EmailVerification.style';

const EmailVerificationDialog = () => {
  const {
    pendingEmail,
    currentStep,
    setCurrentStep,
    initiateEmailChange,
    onValidate,
    onResendCode,
    initiateChangeEmailResult,
    updateUserEmailResult,
  } = useUserVerificationContext();
  const [newEmailState, setNewEmailState] = useState('');
  const [confirmNewEmailState, setConfirmNewEmailState] = useState('');
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const { control, handleSubmit } = useForm();
  const { handleSetSnackbarMessage } = useSnackbarContext();

  const isValidEmail = (email: string): boolean => /\S+@\S+\.\S+/.test(email);
  const handleOnResendCode = () => {
    if (pendingEmail) onResendCode(pendingEmail);
  };
  const handleOnConfirm = () => {
    if (pendingEmail === null) return;
    initiateEmailChange(pendingEmail);
    setCurrentStep(WizardStep.Validate);
  };

  const handleOnEmailChange = () => {
    setEmailValidationError(null);

    if (newEmailState.trim() === '' || confirmNewEmailState.trim() === '') {
      setEmailValidationError('Please fill in both email fields.');
      return;
    }

    if (!isValidEmail(newEmailState)) {
      setEmailValidationError('Please enter a valid email address.');
      return;
    }

    if (newEmailState !== confirmNewEmailState) {
      setEmailValidationError('Email addresses must match.');
      return;
    }

    initiateEmailChange(confirmNewEmailState);
    setCurrentStep(WizardStep.Validate);
  };

  const handleUseDifferentEmail = () => {
    setCurrentStep(WizardStep.Update);
  };

  const handleOnCancel = () => {
    setCurrentStep(WizardStep.Confirm);
    setNewEmailState('');
    setConfirmNewEmailState('');
    setEmailValidationError(null);
  };

  const handleNewEmailState = (event: any) => {
    setNewEmailState(event.target.value);
    if (emailValidationError) {
      setEmailValidationError(null);
    }
  };
  const handleConfirmNewEmailState = (event: any) => {
    setConfirmNewEmailState(event.target.value);
    if (emailValidationError) {
      setEmailValidationError(null);
    }
  };

  const handleOnValidate = async (data: any) => {
    setEmailValidationError(null);
    if (data?.otp === undefined) {
      handleSetSnackbarMessage('Missing or invalid OTP.');
      return;
    }

    const validateEmail = confirmNewEmailState === '' ? pendingEmail || '' : confirmNewEmailState;
    const validateEmailResponse = await onValidate(data.otp, validateEmail);
    if (validateEmailResponse?.errors) setEmailValidationError(ERRORSTATE.INVALID_TWOFA_CODE);
  };

  useEffect(() => {
    if (updateUserEmailResult?.data) {
      setCurrentStep(WizardStep.Deliver);
    }
  }, [updateUserEmailResult]);

  return (
    <>
      {currentStep === WizardStep.Confirm && (
        <>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <StyledDialogContent className="updateEmail-CurrentEmail">
            <Typography color="textSecondary" variant="body3" className="UpdateEmail">
              {dialogSubTitle}
            </Typography>
            <Typography color="textSecondary" variant="body4" className="CurrentEmail">
              {dialogContent}
            </Typography>
            <Typography color="textPrimary" variant="body2" weight="medium" className="Email">
              {pendingEmail}
            </Typography>
            <Typography color="textSecondary" variant="body3">
              {dialogContentTwo}
            </Typography>
          </StyledDialogContent>
          <StyledDialogActions>
            <Button className="dialog-button--different" variant="text" onClick={handleUseDifferentEmail}>
              {dialogContentThree}
            </Button>
            <Button onClick={handleOnConfirm} loading={initiateChangeEmailResult?.loading}>
              {confirmButton}
            </Button>
          </StyledDialogActions>
        </>
      )}

      {currentStep === WizardStep.Update && (
        <>
          <DialogTitle>{dialogTitleTwo}</DialogTitle>
          <StyledDialogContent>
            <form>
              <TextField
                autoFocus
                margin="normal"
                id="email"
                label="Current email"
                className="email-dialog--textfield-input"
                fullWidth
                value={pendingEmail || ''}
                disabled
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                autoFocus
                margin="normal"
                id="email"
                label="New email address"
                fullWidth
                value={newEmailState}
                onChange={handleNewEmailState}
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                margin="normal"
                id="email"
                label="Confirm new email address"
                fullWidth
                value={confirmNewEmailState}
                onChange={handleConfirmNewEmailState}
                inputProps={{ maxLength: 100 }}
              />
            </form>
          </StyledDialogContent>
          <StyledDialogActions>
            <Button onClick={handleOnCancel} variant="text">
              {cancelButton}
            </Button>
            <Button
              onClick={handleOnEmailChange}
              loading={Boolean(initiateChangeEmailResult?.loading)}
              className="dialog-button--validate"
              disabled={
                Boolean(initiateChangeEmailResult?.loading) ||
                newEmailState.trim() === '' ||
                confirmNewEmailState.trim() === '' ||
                !isValidEmail(newEmailState) ||
                newEmailState !== confirmNewEmailState
              }
            >
              {saveButton}
            </Button>
          </StyledDialogActions>
        </>
      )}

      {currentStep === WizardStep.Validate && (
        <>
          <form onSubmit={handleSubmit(handleOnValidate)}>
            <DialogTitle>{dialogTitleThree}</DialogTitle>
            <StyledDialogContent>
              <Controller
                name="otp"
                control={control}
                render={({ field: { onChange, ...field } }) => {
                  const handleChange = (value: string): void => {
                    onChange(value);
                  };
                  return (
                    <OTCInput
                      {...field}
                      autoFocus
                      tabIndex={0}
                      inputMode="numeric"
                      onChange={handleChange}
                      disabled={updateUserEmailResult?.loading}
                      className="otc-input"
                      data-testid="code-container"
                    />
                  );
                }}
              />

              <Typography variant="body3" className="otc-text UpdateEmail">
                {emailValidationError && (
                  <Alert open severity="error" className="email-dialog--email-alert-error">
                    {ERRORSTATE.INVALID_TWOFA_CODE}
                  </Alert>
                )}
              </Typography>
              <Typography color="textSecondary" variant="body3" className="otc-text">
                {otcDialogContenet}
              </Typography>
            </StyledDialogContent>
            <StyledDialogActions>
              <Button onClick={handleOnResendCode} className="dialog-button--validate" variant="text">
                {newCodeButton}
              </Button>
              <Button type="submit" loading={Boolean(updateUserEmailResult?.loading)}>
                {validateButton}
              </Button>
            </StyledDialogActions>
          </form>
        </>
      )}
    </>
  );
};

export default EmailVerificationDialog;
